import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import type { RemoteDataset } from '../samples/catalog';
import { DatasetPicker } from './DatasetPicker';

interface ToolbarProps {
  canExport: boolean;
  canImport: boolean;
  canSample: boolean;
  sampleExists: boolean;
  remoteBusy: boolean;
  onExport: () => void;
  onImport: (file: File) => void;
  onLoadSample: () => void;
  onPickRemote: (ds: RemoteDataset) => void;
  onCustomUrl: (url: string) => void;
}

interface IconButtonProps {
  onClick: () => void;
  disabled?: boolean;
  title: string;
  ariaLabel?: string;
  children: ReactNode;
  caret?: boolean;
  active?: boolean;
}

function IconButton({
  onClick,
  disabled,
  title,
  ariaLabel,
  children,
  caret,
  active,
}: IconButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      aria-label={ariaLabel ?? title}
      className={
        'flex items-center gap-1 px-2 py-1 text-[11px] text-[#bbbbbb] transition-colors hover:bg-[#4c5052] hover:text-[#e8e8e8] disabled:cursor-not-allowed disabled:bg-transparent disabled:text-[#606366] ' +
        (active ? 'bg-[#4c5052] text-[#e8e8e8]' : '')
      }
    >
      {children}
      {caret && <span className="text-[8px]">▾</span>}
    </button>
  );
}

function SampleIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
      <ellipse cx="6" cy="3" rx="4" ry="1.4" fill="none" stroke="currentColor" strokeWidth="1.2" />
      <path
        d="M2 3 V8 C2 9 4 9.5 6 9.5 C8 9.5 10 9 10 8 V3"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.2"
      />
      <path
        d="M7.2 5.6 L8.6 7 L11 4.6"
        fill="none"
        stroke="#499c54"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function GitHubIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 16 16" aria-hidden="true">
      <path
        fill="currentColor"
        d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82.64-.18 1.32-.27 2-.27.68 0 1.36.09 2 .27 1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.013 8.013 0 0 0 16 8c0-4.42-3.58-8-8-8z"
      />
    </svg>
  );
}

function ImportIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
      <path
        d="M6 1 V7 M3 4 L6 7 L9 4"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M2 9 V11 H10 V9"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
      />
    </svg>
  );
}

function ExportIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
      <path
        d="M6 7 V1 M3 4 L6 1 L9 4"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M2 9 V11 H10 V9"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
      />
    </svg>
  );
}

function InfoIcon() {
  return (
    <svg width="11" height="11" viewBox="0 0 12 12" aria-hidden="true">
      <circle cx="6" cy="6" r="5" fill="none" stroke="currentColor" strokeWidth="1.2" />
      <line
        x1="6"
        y1="5"
        x2="6"
        y2="9"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
      />
      <circle cx="6" cy="3.4" r="0.7" fill="currentColor" />
    </svg>
  );
}

export function Toolbar({
  canExport,
  canImport,
  canSample,
  sampleExists,
  remoteBusy,
  onExport,
  onImport,
  onLoadSample,
  onPickRemote,
  onCustomUrl,
}: ToolbarProps) {
  const fileRef = useRef<HTMLInputElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const infoBtnRef = useRef<HTMLButtonElement>(null);
  const datasetsBtnRef = useRef<HTMLDivElement>(null);
  // Anchor for the dataset picker. Like the info popover below, it's portaled
  // to body and positioned fixed — the toolbar's overflow-hidden would clip
  // an absolutely-positioned child dropdown into invisibility.
  const [datasetAnchor, setDatasetAnchor] = useState<{
    right: number;
    top: number;
  } | null>(null);
  const [importChooserOpen, setImportChooserOpen] = useState(false);
  const [chooserDragOver, setChooserDragOver] = useState(false);
  // Anchor for the info popover. Captured from the trigger's bounding rect
  // so the portaled popover lands directly under the ⓘ button.
  const [infoAnchor, setInfoAnchor] = useState<{
    right: number;
    top: number;
  } | null>(null);

  const toggleDatasets = () => {
    if (datasetAnchor) {
      setDatasetAnchor(null);
      return;
    }
    const el = datasetsBtnRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setDatasetAnchor({
      right: window.innerWidth - rect.right,
      top: rect.bottom + 4,
    });
  };

  const toggleInfo = () => {
    if (infoAnchor) {
      setInfoAnchor(null);
      return;
    }
    const btn = infoBtnRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    setInfoAnchor({
      right: window.innerWidth - rect.right,
      top: rect.bottom + 4,
    });
  };

  // External-system effect: dismiss the import-info popover on outside-click
  // or Escape. Listening on document because the popover lives in a portal
  // outside our subtree.
  useEffect(() => {
    if (!infoAnchor) return;
    const onDocClick = (e: MouseEvent) => {
      if (popRef.current?.contains(e.target as Node)) return;
      if (infoBtnRef.current?.contains(e.target as Node)) return;
      setInfoAnchor(null);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setInfoAnchor(null);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [infoAnchor]);

  return (
    <div className="flex items-center divide-x divide-[#4a4a4a] overflow-hidden rounded border border-[#4a4a4a] bg-[#3c3f41]">
      <IconButton
        onClick={onLoadSample}
        disabled={!canSample}
        title={sampleExists ? 'Switch to sample database' : 'Create sample database'}
      >
        <SampleIcon />
        <span>{sampleExists ? 'Open sample' : 'Sample'}</span>
      </IconButton>

      <div ref={datasetsBtnRef} className="relative flex">
        <IconButton
          onClick={toggleDatasets}
          disabled={!canImport || remoteBusy}
          title="Curated GitHub datasets"
          active={!!datasetAnchor}
          caret
        >
          <GitHubIcon />
          <span>{remoteBusy ? 'Downloading…' : 'Datasets'}</span>
        </IconButton>
        {datasetAnchor &&
          typeof document !== 'undefined' &&
          createPortal(
            <DatasetPicker
              open
              busy={remoteBusy}
              anchor={datasetAnchor}
              triggerRef={datasetsBtnRef}
              onClose={() => setDatasetAnchor(null)}
              onPick={(ds) => {
                setDatasetAnchor(null);
                onPickRemote(ds);
              }}
              onCustomUrl={onCustomUrl}
            />,
            document.body,
          )}
      </div>

      <div className="relative flex">
        <IconButton
          onClick={() => setImportChooserOpen(true)}
          disabled={!canImport}
          title="Import a SQLite file (file picker or drag-and-drop)"
        >
          <ImportIcon />
          <span>Import</span>
        </IconButton>
        <button
          ref={infoBtnRef}
          type="button"
          onClick={toggleInfo}
          disabled={!canImport}
          aria-label="Import details"
          title="What can I import?"
          className="border-l border-[#4a4a4a] px-1.5 text-[#808080] hover:bg-[#4c5052] hover:text-[#bbbbbb] disabled:cursor-not-allowed disabled:opacity-40"
        >
          <InfoIcon />
        </button>
        {infoAnchor &&
          typeof document !== 'undefined' &&
          createPortal(
            <div
              ref={popRef}
              className="fixed z-50 w-72 rounded border border-[#4a4a4a] bg-[#2b2b2b] p-3 text-[11px] leading-relaxed text-[#bbbbbb] shadow-xl"
              style={{ right: infoAnchor.right, top: infoAnchor.top }}
            >
              <div className="mb-2 text-[12px] font-semibold text-[#e8e8e8]">
                Importing a database
              </div>
              <p className="mb-2">
                Pick any SQLite file (extensions <code className="text-[#6a8759]">.db</code>,{' '}
                <code className="text-[#6a8759]">.sqlite</code>,{' '}
                <code className="text-[#6a8759]">.sqlite3</code>) — it&apos;s copied byte-for-byte
                into browser storage and opens like any other database in the sidebar.
              </p>
              <p className="mb-2">
                <span className="text-[#e8e8e8]">Two ways to import:</span> use the{' '}
                <span className="text-[#e8e8e8]">Import</span> button for a file picker, or just{' '}
                <span className="text-[#e8e8e8]">drag &amp; drop</span> a SQLite file from your
                computer onto this window — works any time, you don&apos;t need to click Import
                first.
              </p>
              <p className="mb-2 text-[#808080]">
                Want a meatier dataset without the round-trip? Use{' '}
                <span className="text-[#bbbbbb]">Datasets</span> to pull Chinook or Northwind
                directly from GitHub.
              </p>
              <p className="text-[10px] text-[#606366]">
                All processing is local. Nothing is uploaded — there&apos;s no server to upload to.
              </p>
            </div>,
            document.body,
          )}
      </div>

      <IconButton
        onClick={onExport}
        disabled={!canExport}
        title="Download the active database as a .db file"
      >
        <ExportIcon />
        <span>Export</span>
      </IconButton>

      <input
        ref={fileRef}
        type="file"
        accept=".db,.sqlite,.sqlite3,application/x-sqlite3,application/octet-stream"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) {
            setImportChooserOpen(false);
            onImport(file);
          }
          e.target.value = '';
        }}
      />
      {importChooserOpen &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4"
            onClick={() => {
              setImportChooserOpen(false);
              setChooserDragOver(false);
            }}
          >
            <div
              className="w-full max-w-md rounded-lg border border-[#4a4a4a] bg-[#3c3f41] p-5 shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="mb-1 flex items-center justify-between">
                <h2 className="text-[14px] font-semibold text-[#e8e8e8]">
                  Import a SQLite database
                </h2>
                <button
                  type="button"
                  onClick={() => {
                    setImportChooserOpen(false);
                    setChooserDragOver(false);
                  }}
                  aria-label="Close"
                  className="rounded p-1 text-[#808080] hover:bg-[#4c5052] hover:text-[#e8e8e8]"
                >
                  <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
                    <path
                      d="M2 2 L10 10 M10 2 L2 10"
                      stroke="currentColor"
                      strokeWidth="1.6"
                      strokeLinecap="round"
                    />
                  </svg>
                </button>
              </div>
              <p className="mb-4 text-[11px] leading-relaxed text-[#808080]">
                Pick a <code className="text-[#6a8759]">.db</code>,{' '}
                <code className="text-[#6a8759]">.sqlite</code>, or{' '}
                <code className="text-[#6a8759]">.sqlite3</code> file — stays local, never uploaded.
              </p>
              <div
                onDragEnter={(e) => {
                  if (!Array.from(e.dataTransfer?.types ?? []).includes('Files')) return;
                  e.preventDefault();
                  setChooserDragOver(true);
                }}
                onDragOver={(e) => {
                  if (!Array.from(e.dataTransfer?.types ?? []).includes('Files')) return;
                  e.preventDefault();
                  e.dataTransfer.dropEffect = 'copy';
                }}
                onDragLeave={() => setChooserDragOver(false)}
                onDrop={(e) => {
                  if (!Array.from(e.dataTransfer?.types ?? []).includes('Files')) return;
                  e.preventDefault();
                  e.stopPropagation();
                  setChooserDragOver(false);
                  const file = e.dataTransfer.files?.[0];
                  if (file) {
                    setImportChooserOpen(false);
                    onImport(file);
                  }
                }}
                className={
                  'flex flex-col items-center gap-3 rounded-lg border-2 border-dashed p-6 text-center transition-colors ' +
                  (chooserDragOver
                    ? 'border-[#499c54] bg-[#2b2b2b]'
                    : 'border-[#4a4a4a] bg-[#2b2b2b]/60')
                }
              >
                <svg
                  width="36"
                  height="36"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                  className="text-[#808080]"
                >
                  <path
                    d="M12 3 V15 M7 10 L12 15 L17 10"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M4 18 V20 H20 V18"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                  />
                </svg>
                <div className="text-[12px] text-[#bbbbbb]">
                  {chooserDragOver ? 'Release to import' : 'Drag & drop a SQLite file here'}
                </div>
                <div className="text-[10px] text-[#606366]">— or —</div>
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  className="rounded bg-[#499c54] px-3 py-1.5 text-[12px] font-medium text-white hover:bg-[#5fb069]"
                >
                  Choose file…
                </button>
              </div>
              <p className="mt-3 text-[10px] leading-relaxed text-[#606366]">
                Tip: drag-and-drop also works any time without opening this dialog — drop a SQLite
                file anywhere on the YesQL window.
              </p>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}
